import { useRef, useCallback } from 'react';

/**
 * 이미지 레지스트리 항목
 */
export interface ImageRegistryEntry {
  image: HTMLImageElement;
  src: string;
  fileName?: string;
  mimeType?: string;
}

/**
 * 이미지 레지스트리 훅
 * 같은 해시의 이미지를 재사용하여 메모리 최적화
 */
export function useImageRegistry() {
  const registryRef = useRef<Map<string, ImageRegistryEntry>>(new Map());

  /**
   * 해시로 이미지 가져오기
   */
  const getImage = useCallback((hash: string): ImageRegistryEntry | null => {
    return registryRef.current.get(hash) || null;
  }, []);

  /**
   * 이미지 등록
   */
  const registerImage = useCallback((hash: string, entry: ImageRegistryEntry) => {
    // 이미 등록된 경우 재사용 (메모리 절약)
    if (!registryRef.current.has(hash)) {
      registryRef.current.set(hash, entry);
    }
  }, []);

  /**
   * 이미지 로드 (해시가 있으면 재사용)
   */
  const loadImage = useCallback(async (
    hash: string,
    src: string,
    fileName?: string
  ): Promise<HTMLImageElement> => {
    // 레지스트리에 있으면 재사용
    const existing = registryRef.current.get(hash);
    if (existing) {
      return existing.image;
    }

    // 새로 로드
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        registerImage(hash, { image: img, src, fileName });
        resolve(img);
      };
      img.onerror = () => {
        reject(new Error('이미지 로드 실패'));
      };
      img.src = src;
    });
  }, [registerImage]);

  /**
   * 레지스트리 초기화 (메모리 정리)
   */
  const clearRegistry = useCallback(() => {
    registryRef.current.clear();
  }, []);

  /**
   * 사용하지 않는 이미지 제거 (선택적)
   */
  const cleanupUnused = useCallback((usedHashes: Set<string>) => {
    const toDelete: string[] = [];
    registryRef.current.forEach((_, hash) => {
      if (!usedHashes.has(hash)) {
        toDelete.push(hash);
      }
    });
    toDelete.forEach(hash => registryRef.current.delete(hash));
  }, []);

  return {
    getImage,
    registerImage,
    loadImage,
    clearRegistry,
    cleanupUnused
  };
}

